/**
 * How Dossiers reads: the reply deadline as a live pill, channels, statuses,
 * durations and timeline headings, all in the staff member's language.
 */
import type {
  SupportCaseChannel,
  SupportCaseDetail,
  SupportCaseEntry,
  SupportCaseListItem,
  SupportCasePriority,
  SupportCaseSmsStatus,
  SupportCaseStatus,
} from "@wallyo/contracts/console/cases";
import { t, type MessageId } from "../../i18n";
import { formatNumber } from "../../i18n/format";
import { formatWhen } from "../audit-format";

export type Tone = "bad" | "warn" | "info" | "ok" | "mute";

/** Within this many minutes of its deadline, a case turns amber. */
export const DEADLINE_WARN_MINUTES = 30;

const MINUTE = 60_000;

/** "35 min", "3 h 10 min", "2 h": whole minutes, through the catalog's units and Intl. */
export function formatMinutes(total: number): string {
  const minutes = Math.max(0, Math.round(total));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const h = hours > 0 ? t("unit.hours", { value: formatNumber(hours) }) : null;
  const m = rest > 0 || hours === 0 ? t("unit.minutes", { value: formatNumber(rest) }) : null;
  return [h, m].filter(Boolean).join(" ");
}

/** A call's length: "45 s" under a minute, else rounded to the minute. */
export function formatCallDuration(seconds: number): string {
  if (seconds < 60) return t("unit.seconds", { value: formatNumber(Math.max(0, seconds)) });
  return formatMinutes(seconds / 60);
}

export type DeadlineState = "closed" | "replied" | "late" | "soon" | "due";

/**
 * Where a case stands against its first-reply deadline, now. Computed on the
 * screen from `respondBy` so the pill counts down live; the server's `late`
 * is the same rule at the moment it answered.
 */
export function deadlineState(
  item: Pick<SupportCaseListItem, "status" | "firstRepliedAt" | "respondBy">,
  now: Date,
): { state: DeadlineState; minutes: number } {
  if (item.status === "closed") return { state: "closed", minutes: 0 };
  if (item.firstRepliedAt) return { state: "replied", minutes: 0 };
  const left = (new Date(item.respondBy).getTime() - now.getTime()) / MINUTE;
  if (left <= 0) return { state: "late", minutes: Math.floor(-left) };
  return { state: left <= DEADLINE_WARN_MINUTES ? "soon" : "due", minutes: Math.ceil(left) };
}

const DEADLINE_TONES: Record<DeadlineState, Tone> = {
  closed: "mute",
  replied: "ok",
  late: "bad",
  soon: "warn",
  due: "info",
};

/** The queue's deadline pill: red when late, amber within 30 minutes. */
export function deadlinePill(
  item: Pick<SupportCaseListItem, "status" | "firstRepliedAt" | "respondBy">,
  now: Date,
): { tone: Tone; label: string; state: DeadlineState } {
  const { state, minutes } = deadlineState(item, now);
  const label =
    state === "closed"
      ? t("cases.deadline.closed")
      : state === "replied"
        ? t("cases.deadline.replied")
        : state === "late"
          ? t("cases.deadline.late", { time: formatMinutes(minutes) })
          : t("cases.deadline.left", { time: formatMinutes(minutes) });
  return { tone: DEADLINE_TONES[state], label, state };
}

export const CHANNEL_ICONS: Record<SupportCaseChannel, string> = { phone: "📞", email: "✉️", chat: "💬" };

export const CHANNEL_LABELS: Record<SupportCaseChannel, MessageId> = {
  phone: "cases.channel.phone",
  email: "cases.channel.email",
  chat: "cases.channel.chat",
};

export const STATUS_LABELS: Record<SupportCaseStatus, MessageId> = {
  open: "cases.status.open",
  waiting_on_customer: "cases.status.waiting_on_customer",
  closed: "cases.status.closed",
};

export const STATUS_TONES: Record<SupportCaseStatus, Tone> = {
  open: "info",
  waiting_on_customer: "warn",
  closed: "mute",
};

export const PRIORITY_LABELS: Record<SupportCasePriority, MessageId> = {
  low: "cases.priority.low",
  normal: "cases.priority.normal",
  high: "cases.priority.high",
  urgent: "cases.priority.urgent",
};

export const SMS_STATUS: Record<SupportCaseSmsStatus, { label: MessageId; tone: Tone }> = {
  pending: { label: "cases.sms.pending", tone: "warn" },
  sent: { label: "cases.sms.sent", tone: "ok" },
  failed: { label: "cases.sms.failed", tone: "bad" },
};

/** The customer's name, or "Appelant inconnu" until the case is linked to someone. */
export function customerLabel(item: Pick<SupportCaseListItem, "customer">): string {
  return item.customer?.name || t("cases.unknownCaller");
}

/** The case header's customer line: their name and masked phone, or the number an unknown caller gave. */
export function customerLine(detail: SupportCaseDetail): string {
  if (detail.customer) {
    return detail.customer.maskedPhone
      ? t("cases.customerWithPhone", { name: customerLabel(detail), phone: detail.customer.maskedPhone })
      : customerLabel(detail);
  }
  return detail.maskedContactPhone
    ? t("cases.unknownCallerPhone", { phone: detail.maskedContactPhone })
    : t("cases.unknownCaller");
}

export function ownerLabel(item: Pick<SupportCaseListItem, "owner">): string {
  return item.owner ? t("cases.ownedBy", { name: item.owner.name }) : t("cases.unassigned");
}

/** "09:31", or "17 sept. 09:31" when it wasn't today. */
export function entryTime(iso: string, now: Date): string {
  const { time, day } = formatWhen(iso, now);
  return day ? `${day} ${time}` : time;
}

/** What an entry is: "Appel (4 min)", "Note interne", "SMS au +224 6•• •• •• 41"… */
export function entryHeading(entry: SupportCaseEntry): string {
  const fromCustomer = entry.author.type === "customer";
  switch (entry.type) {
    case "call":
      return entry.callDurationSeconds === null
        ? t("cases.entry.call")
        : t("cases.entry.callWithDuration", { duration: formatCallDuration(entry.callDurationSeconds) });
    case "note":
      return t("cases.entry.note");
    case "chat":
      return fromCustomer ? t("cases.entry.chatIn") : t("cases.entry.chatOut");
    case "sms":
      if (fromCustomer) return t("cases.entry.smsIn");
      return entry.sms ? t("cases.entry.smsOutTo", { phone: entry.sms.maskedTo }) : t("cases.entry.smsOut");
    case "email":
      return fromCustomer ? t("cases.entry.emailIn") : t("cases.entry.emailOut");
  }
}

/** An entry's look on the timeline: staff entries in bronze, internal notes set apart. */
export function entryClass(entry: SupportCaseEntry): string {
  return [
    "ev",
    entry.author.type === "staff" ? "staff" : "customer",
    entry.visibility === "internal" ? "internal" : "",
    entry.type === "note" ? "case-note" : "",
    entry.sms?.status === "failed" ? "refused" : "",
  ]
    .filter(Boolean)
    .join(" ");
}
